@if (pageLoading) {
  <app-loading-overlay [visible]="true" [indeterminate]="true"></app-loading-overlay>
} @else if (currentFund) {
  <form [formGroup]="transferForm">

    <!-- ================================================================ -->
    <!-- SECTION 1: SELECT FUND TO RECEIVE TRANSFER -->
    <!-- ================================================================ -->
    <div class="form-section">
      <div class="section-header">
        <span class="section-number">1</span>
        <span class="section-title">{{ 'transfer.form.receiver.title' | translateLang }}</span>
      </div>

      <!-- My Fund / Other Fund Radio (DAF only) -->
      @if (showFundRadio) {
        <div class="field-group">
          <div class="radio-group">
            <div class="radio-option">
              <p-radiobutton
                formControlName="receiverFundType"
                value="myFund"
                name="receiverFundType"
                inputId="receiver-my-fund"
              />
              <label for="receiver-my-fund" class="radio-label">{{ 'transfer.form.receiver.my_fund' | translateLang }}</label>
            </div>
            @if (showOtherFundOption) {
              <div class="radio-option">
                <p-radiobutton
                  formControlName="receiverFundType"
                  value="otherFund"
                  name="receiverFundType"
                  inputId="receiver-other-fund"
                />
                <label for="receiver-other-fund" class="radio-label">{{ 'transfer.form.receiver.other_fund' | translateLang }}</label>
              </div>
            }
          </div>
        </div>
      }

      <!-- My Fund Selection (dropdown) -->
      @if (receiverFundType.value === 'myFund') {
        <div class="field-group">
          <div class="field-label-row">
            <label class="field-label">
              {{ 'transfer.form.receiver.choose_fund' | translateLang }}<span class="required-asterisk">*</span>
            </label>
            <span class="required-hint">{{ 'grant.form.required_field' | translateLang }}</span>
          </div>
          @if (loadingFunds) {
            <small class="checking-text">{{ 'transfer.form.loading_funds' | translateLang }}</small>
          } @else if (showOneFundInfo) {
            <div class="info-box">{{ 'transfer.form.one_fund_info' | translateLang }}</div>
          } @else if (availableFunds.length === 0) {
            <small class="helper-text">{{ 'transfer.form.no_funds_available' | translateLang }}</small>
          } @else {
            <p-select
              formControlName="myFund"
              [options]="availableFunds"
              optionLabel="displayName"
              [placeholder]="'transfer.form.receiver.select_placeholder' | translateLang"
              [showClear]="!myFundDisabled"
              [disabled]="myFundDisabled"
              styleClass="w-full"
              (onChange)="onMyFundChange($event)"
            />
          }
          @if (myFund.invalid && (myFund.dirty || myFund.touched)) {
            <small class="error-text">{{ 'validation.required' | translateLang }}</small>
          }
        </div>
      }

      <!-- Other Fund Inputs (DAF only) -->
      @if (showOtherFundOption && receiverFundType.value === 'otherFund') {
        <div class="field-group-row">
          <div class="field-group field-group--half">
            <div class="field-label-row">
              <label class="field-label">
                {{ 'transfer.form.receiver.other_fund_number' | translateLang }}<span class="required-asterisk">*</span>
              </label>
            </div>
            <input
              type="text"
              class="transfer-input"
              formControlName="otherFundNumber"
              [placeholder]="'transfer.form.receiver.fund_number_placeholder' | translateLang"
            />
            @if (checkingFundNumber) {
              <small class="checking-text">{{ 'transfer.form.checking_fund' | translateLang }}</small>
            }
            @if (otherFundNumber.invalid && (otherFundNumber.dirty || otherFundNumber.touched)) {
              <small class="error-text">
                @if (otherFundNumber.errors?.['required']) {
                  {{ 'validation.required' | translateLang }}
                } @else if (otherFundNumber.errors?.['specialChars']) {
                  {{ 'transfer.form.receiver.error_special_chars' | translateLang }}
                } @else if (otherFundNumber.errors?.['sameFund']) {
                  {{ 'transfer.form.receiver.error_same_fund' | translateLang }}
                } @else if (otherFundNumber.errors?.['fundNotFound']) {
                  {{ 'transfer.form.receiver.error_fund_not_found' | translateLang }}
                } @else if (otherFundNumber.errors?.['ineligibleFund']) {
                  {{ 'transfer.form.receiver.error_ineligible_fund' | translateLang }}
                } @else if (otherFundNumber.errors?.['fundIsCharity']) {
                  {{ 'transfer.form.receiver.error_fund_is_charity' | translateLang }}
                } @else if (otherFundNumber.errors?.['serverError']) {
                  {{ 'transfer.form.receiver.error_server' | translateLang }}
                }
              </small>
            }
          </div>
          <div class="field-group field-group--half">
            <div class="field-label-row">
              <label class="field-label">
                {{ 'transfer.form.receiver.other_fund_name' | translateLang }}<span class="required-asterisk">*</span>
              </label>
            </div>
            <input
              type="text"
              class="transfer-input"
              formControlName="otherFundName"
              [placeholder]="'transfer.form.receiver.fund_name_placeholder' | translateLang"
            />
            @if (otherFundName.invalid && (otherFundName.dirty || otherFundName.touched)) {
              <small class="error-text">{{ 'validation.required' | translateLang }}</small>
            }
          </div>
        </div>
      }
    </div>

    <hr class="section-divider" />

    <!-- ================================================================ -->
    <!-- SECTION 2: DETAILS (Amount + Timing) -->
    <!-- ================================================================ -->
    <div class="form-section">
      <div class="section-header">
        <span class="section-number">2</span>
        <span class="section-title">{{ 'transfer.form.details.title' | translateLang }}</span>
      </div>

      <!-- Amount -->
      <div class="field-group">
        <div class="field-label-row">
          <label class="field-label">
            {{ 'contribute.section4.amount.label' | translateLang }}<span class="required-asterisk">*</span>
          </label>
          <span class="required-hint">{{ 'grant.form.required_field' | translateLang }}</span>
        </div>
        <div class="amount-input-container">
          <div class="amount-section">
            <input
              type="text"
              class="p-inputtext w-full"
              [value]="displayAmount"
              [placeholder]="'0' + decimalSymbol + '00'"
              (input)="onAmountInput($event)"
              (blur)="onAmountBlur($event)"
              (keydown)="onAmountKeyDown($event)"
            />
          </div>
          <div class="currency-section">
            <span class="currency-tag">{{ displayCurrencySymbol || currentFund.currencySymbol }}</span>
          </div>
        </div>
        @if (amount.invalid && (amount.dirty || amount.touched)) {
          <small class="error-text">
            @if (amount.errors?.['required']) {
              {{ 'validation.required' | translateLang }}
            } @else if (amount.errors?.['maxAmountAllowed']) {
              {{ 'grant.form.amount.error_max' | translateLang }}
            } @else if (amount.errors?.['noDecimals']) {
              {{ 'grant.form.amount.error_no_decimals' | translateLang }}
            }
          </small>
        }
        <p class="field-helper">
          {{ 'grant.form.amount.available' | translateLang }}
          <strong>{{ formatNumberValue(availableAmountToDisplay) }}</strong>
          {{ displayCurrencySymbol || currentFund.currencySymbol }}
        </p>
      </div>

      <!-- Timing -->
      <div class="field-group mt-4">
        <div class="field-label-row">
          <label class="field-label">
            {{ 'grant.form.timing.label' | translateLang }}<span class="required-asterisk">*</span>
          </label>
        </div>
        <div class="radio-group">
          <div class="radio-option">
            <p-radiobutton
              formControlName="timing"
              value="asap"
              name="timing"
              inputId="timing-asap"
            />
            <label for="timing-asap" class="radio-label">{{ 'grant.form.timing.asap' | translateLang }}</label>
          </div>
          <div class="radio-option">
            <p-radiobutton
              formControlName="timing"
              value="specificDate"
              name="timing"
              inputId="timing-specific-date"
            />
            <label for="timing-specific-date" class="radio-label">{{ 'grant.form.timing.specific_date' | translateLang }}</label>
          </div>
        </div>
        @if (timing.invalid && (timing.dirty || timing.touched)) {
          <small class="error-text">{{ 'validation.required' | translateLang }}</small>
        }
      </div>

      <!-- Date Picker -->
      @if (timing.value === 'specificDate') {
        <div class="field-group field-group--half mt-3">
          <label class="field-label" for="specific-date-input">
            {{ 'grant.form.timing.date_label' | translateLang }}<span class="required-asterisk">*</span>
          </label>
          <p-datepicker
            formControlName="date"
            [minDate]="minDate"
            [dateFormat]="dateFormatPrimeNG"
            [placeholder]="dateFormatPlaceholder"
            [showIcon]="true"
            inputId="specific-date-input"
            styleClass="w-full"
          />
          @if (date.invalid && (date.dirty || date.touched)) {
            <small class="error-text">{{ 'validation.required' | translateLang }}</small>
          }
        </div>
      }
    </div>

    <hr class="section-divider" />

    <!-- ================================================================ -->
    <!-- SECTION 3: INSTRUCTIONS (Optional) -->
    <!-- ================================================================ -->
    <div class="form-section">
      <div class="section-header">
        <span class="section-number">3</span>
        <span class="section-title">{{ 'grant.form.instructions.title' | translateLang }}</span>
        <span class="section-optional">({{ 'general.optional' | translateLang }})</span>
      </div>

      <div class="field-group">
        <div class="checkbox-option mb-2">
          <p-checkbox
            formControlName="showAdditionalNotes"
            [binary]="true"
            inputId="show-additional-notes"
          />
          <label for="show-additional-notes" class="checkbox-label">
            {{ 'transfer.form.instructions.org_label' | translateLang }}
            <strong>{{ instanceName }}</strong>
          </label>
        </div>
        @if (showAdditionalNotes.value) {
          <textarea
            class="transfer-textarea"
            formControlName="additionalNotes"
            [placeholder]="'transfer.form.instructions.placeholder' | translateLang"
          ></textarea>
          @if (additionalNotes.invalid && (additionalNotes.dirty || additionalNotes.touched)) {
            <small class="error-text">
              @if (additionalNotes.errors?.['required']) {
                {{ 'transfer.form.instructions.error_required' | translateLang }}
              } @else if (additionalNotes.errors?.['minLength']) {
                {{ 'transfer.form.instructions.error_min' | translateLang }}
              } @else if (additionalNotes.errors?.['maxLength']) {
                {{ 'transfer.form.instructions.error_max' | translateLang }}
              }
            </small>
          }
        }
      </div>
    </div>

  </form>
}
